/**
 * 💳 PurchaseService - Google Play Billing Integration
 * 
 * Verwendet cordova-plugin-purchase für direkte Google Play Billing API
 * Kein Drittanbieter-Service nötig!
 * 
 * Product IDs (in Google Play Console konfiguriert):
 * - premium_monthly: €19.99/Monat
 * - premium_yearly: €199.99/Jahr
 */

import { Capacitor } from '@capacitor/core';
import { verifyPurchase } from './serverDataService';
import { reportError } from './errorReport'; // 🧯 Kauf-Probleme fürs Admin-Board
import { tGlobal } from '../utils/tGlobal';

// 🔒 Kauf-Token aus einer Transaktion ziehen und zur SERVER-Prüfung schicken (defensiv).
// Der Server fragt Google (subscriptionsv2) und schaltet mit echtem Ablaufdatum frei + acknowledged.
const zuletztGemeldet = {}; // purchaseToken → Zeitpunkt (gegen Doppel-Meldungen: approved + direkter Abgleich)
async function syncTransactionToServer(transaction) {
  try {
    const np = transaction?.nativePurchase || {};
    const purchaseToken = np.purchaseToken || transaction?.purchaseToken || transaction?.transactionId || null;
    const productId = (transaction?.products && transaction.products[0] && transaction.products[0].id)
      || (Array.isArray(np.productIds) && np.productIds[0]) || null;
    if (!purchaseToken) { console.warn('💳 Kein purchaseToken in Transaktion — Server-Verify übersprungen'); return false; }
    if (Date.now() - (zuletztGemeldet[purchaseToken] || 0) < 20000) return false;
    zuletztGemeldet[purchaseToken] = Date.now();
    const res = await verifyPurchase({ purchaseToken, productId });
    console.log('💳 Server-Verify:', res);
    try { window.dispatchEvent(new CustomEvent('kiosk:premium-refresh')); } catch { /* */ }
    return !!res?.success;
  } catch (e) {
    console.warn('💳 Server-Verify fehlgeschlagen (kein Abbruch):', e?.message || e);
    reportError('kauf_meldung_fehler', e);
    return false;
  }
}

/**
 * 🔁 Bezahlte Käufe DIREKT aus den lokalen Play-Belegen an den Server melden — unabhängig vom
 * `.approved`-Ereignis. Grund (01.10.2026, Green Goat): nach dem Bezahlen kam 40 s lang kein
 * /verify am Server an; freigeschaltet wurde erst beim nächsten App-Start (restore beim Init).
 * Wird nach einem Kauf mehrfach und beim Zurückkehren in die App aufgerufen, solange kein Abo aktiv ist.
 * @returns {Promise<boolean>} true, wenn der Server ein aktives Abo bestätigt hat
 */
let abgleichLaeuft = false;
export async function syncOwnedToServer({ refresh = true } = {}) {
  if (!isStoreReady || !store || abgleichLaeuft) return false;
  abgleichLaeuft = true;
  try {
    if (refresh) {
      try { await store.restorePurchases(); } catch (e) { console.warn('💳 restorePurchases:', e?.message); }
    }
    let ok = false;
    for (const receipt of (store.localReceipts || [])) {
      for (const tr of (receipt.transactions || [])) {
        const np = tr?.nativePurchase;
        // getPurchaseState 1 = PURCHASED (2 = PENDING → noch nicht bezahlt, nicht melden)
        if (!np?.purchaseToken || np.getPurchaseState !== 1) continue;
        if (await syncTransactionToServer(tr)) ok = true;
      }
    }
    return ok;
  } catch (e) {
    console.warn('💳 syncOwnedToServer:', e?.message || e);
    return false;
  } finally {
    abgleichLaeuft = false;
  }
}

// Nach einem Kaufversuch im Hintergrund nachfassen (Bezahlung kann länger dauern als order())
function nachKaufNachfassen() {
  [4000, 12000, 30000, 60000, 120000].forEach((ms) => {
    setTimeout(() => { syncOwnedToServer(); }, ms);
  });
}

// Product IDs müssen mit Google Play Console übereinstimmen
export const PRODUCT_IDS = {
  MONTHLY: 'premium_monthly',
  YEARLY: 'premium_yearly',
};

// Google Play Lizenz-Schlüssel (Public Key aus Play Console → Monetarisierung)
const GOOGLE_PLAY_LICENSE_KEY = 'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAuU46CSOsOpjhjQYaVepi+KSmkfln6RE+xs8WScRCtpb/NxADI4VXd3vALcYho1KbYajoENt/dwaR4DISIDF5Z1WoD0slFOJRQZvl4YI2xcGmdnuLrewbEze3Dp165kFclCCqYqZ/xTtRQzgutHmvPjRhwpB+WU049KnUxDJdiNdema8ht1GeiOe0gZOlojqm+hKF4x5OQ+wH7p2WHQlwcHfL+CiojT+Qw4T9xSUixNPJ3E9XdspA8IyggYPEO0gGuUcvIg79NK7xqYTrnT1pbOPP51tcmWxGg0VMKABN4HyzXOIjarRg6+yfyp0QK/Tkghp4JBxm3kSS/n/uAdXqUQIDAQAB';

// Store-Referenz (wird bei init gesetzt)
let store = null;
let isStoreReady = false;
let productCache = {};

/**
 * Initialisiert den In-App Purchase Store
 * Muss einmal beim App-Start aufgerufen werden
 */
export async function initializePurchases(userEmail) {
  // Nur auf nativen Plattformen (Android/iOS)
  if (!Capacitor.isNativePlatform()) {
    console.log('💳 PurchaseService: Web-Modus - keine In-App Käufe');
    return false;
  }

  // Warte auf deviceready (Cordova/Capacitor)
  if (!window.CdvPurchase) {
    console.log('💳 Warte auf CdvPurchase Plugin...');
    await new Promise((resolve) => {
      document.addEventListener('deviceready', resolve, false);
      // Fallback timeout
      setTimeout(resolve, 3000);
    });
  }

  if (!window.CdvPurchase) {
    console.error('💳 CdvPurchase Plugin nicht verfügbar');
    return false;
  }

  try {
    store = window.CdvPurchase.store;
    const { ProductType, Platform } = window.CdvPurchase;

    console.log('💳 Initialisiere Google Play Billing...');

    // Validator/Lizenz-Key für Google Play setzen
    store.validator = {
      url: 'https://validator.iaptic.com/', // Optional: Server-Validierung
      // Für lokale Validierung wird der Key bei initialize() übergeben
    };

    // Produkte registrieren
    store.register([
      {
        id: PRODUCT_IDS.MONTHLY,
        type: ProductType.PAID_SUBSCRIPTION,
        platform: Platform.GOOGLE_PLAY,
      },
      {
        id: PRODUCT_IDS.YEARLY,
        type: ProductType.PAID_SUBSCRIPTION,
        platform: Platform.GOOGLE_PLAY,
      },
    ]);

    // Event Handler für Updates
    store.when()
      .productUpdated((product) => {
        console.log('💳 Produkt aktualisiert:', product.id, product.pricing?.price);
        productCache[product.id] = product;
      })
      .approved((transaction) => {
        console.log('💳 Kauf genehmigt:', transaction.products);
        // Transaktion bestätigen (wichtig!)
        transaction.verify();
        // 🔒 Quelle der Wahrheit: Token an unseren Server → prüft bei Google + acknowledge + Premium setzen
        syncTransactionToServer(transaction);
      })
      .verified((receipt) => {
        console.log('💳 Kauf verifiziert:', receipt);
        receipt.finish();
      })
      .finished((transaction) => {
        console.log('💳 Transaktion abgeschlossen:', transaction.products);
      });

    // Store initialisieren
    await store.initialize([Platform.GOOGLE_PLAY]);
    
    // Produkte laden
    await store.update();

    // 🔁 Selbstheilung: aktive Käufe erneut abfragen → löst .approved für Besitz aus, sodass ein
    // früher verpasster Kauf (Server hat ihn nie bekommen) beim App-Start nachträglich verifiziert wird.
    try { await store.restorePurchases(); } catch (e) { console.warn('💳 restorePurchases (init):', e?.message); }

    isStoreReady = true;
    console.log('💳 Google Play Billing erfolgreich initialisiert');
    return true;

  } catch (error) {
    console.error('💳 Initialisierung fehlgeschlagen:', error);
    isStoreReady = false;
    return false;
  }
}

/**
 * Holt verfügbare Produkte/Preise
 * Gibt Produkte mit lokalen Preisen zurück
 */
export async function getProducts() {
  if (!isStoreReady || !store) {
    console.log('💳 getProducts: Store nicht bereit');
    return {
      monthly: { price: '€19,99', priceString: tGlobal('ui.svc.pricePerMonth', { price: '€19,99' }) },
      yearly: { price: '€199,99', priceString: tGlobal('ui.svc.pricePerYear', { price: '€199,99' }) },
    };
  }

  try {
    const monthlyProduct = store.get(PRODUCT_IDS.MONTHLY);
    const yearlyProduct = store.get(PRODUCT_IDS.YEARLY);

    const result = {
      monthly: {
        price: monthlyProduct?.pricing?.price || '€19,99',
        priceString: tGlobal('ui.svc.pricePerMonth', { price: monthlyProduct?.pricing?.price || '€19,99' }),
        product: monthlyProduct,
      },
      yearly: {
        price: yearlyProduct?.pricing?.price || '€199,99',
        priceString: tGlobal('ui.svc.pricePerYear', { price: yearlyProduct?.pricing?.price || '€199,99' }),
        product: yearlyProduct,
      },
    };

    console.log('💳 Produkte geladen:', result);
    return result;

  } catch (error) {
    console.error('💳 Fehler beim Laden der Produkte:', error);
    return null;
  }
}

/**
 * Führt einen Kauf durch
 * @param {'monthly' | 'yearly'} plan - Gewählter Plan
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function purchaseSubscription(plan) {
  if (!isStoreReady || !store) {
    console.error('💳 purchaseSubscription: Store nicht bereit');
    return { 
      success: false, 
      error: tGlobal('ui.svc.purchaseAndroidOnly') 
    };
  }

  const productId = plan === 'monthly' ? PRODUCT_IDS.MONTHLY : PRODUCT_IDS.YEARLY;

  try {
    console.log('💳 Starte Kauf:', productId);
    
    const product = store.get(productId);
    
    if (!product) {
      console.error('💳 Produkt nicht gefunden:', productId);
      reportError('kauf_produkt_fehlt', productId);
      return { success: false, error: tGlobal('ui.svc.productNotFound', { id: productId }) };
    }

    // Kauf-Offer holen (erstes verfügbares)
    const offer = product.getOffer();
    
    if (!offer) {
      console.error('💳 Kein Angebot verfügbar für:', productId);
      return { success: false, error: tGlobal('ui.svc.noOfferAvailable') };
    }

    // Kauf durchführen
    console.log('💳 Starte order() für:', productId);
    
    const result = await offer.order();

    console.log('💳 Order Ergebnis:', result);
    nachKaufNachfassen(); // 🔁 Kauf sicher an den Server melden, auch wenn `.approved` ausbleibt
    
    // Prüfe ob Kauf erfolgreich war
    if (result && result.isError) {
      const errorCode = result.code;
      if (errorCode === 'E_USER_CANCELLED' || errorCode === 6777001) {
        console.log('💳 Kauf vom Benutzer abgebrochen');
        return { success: false, error: 'cancelled' };
      }
      console.error('💳 Kauf fehlgeschlagen:', result);
      reportError('kauf_fehler', result.message || 'Kauf fehlgeschlagen', { code: String(result.code || '') });
      return { success: false, error: result.message || tGlobal('ui.svc.purchaseFailed') };
    }

    // Kauf wurde gestartet - warte auf Bestätigung
    // Der verified/finished Handler in initializePurchases() kümmert sich um den Rest
    console.log('💳 Kauf-Prozess gestartet, warte auf Bestätigung...');
    
    // Warte kurz und prüfe dann ob Produkt jetzt owned ist
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Update store um aktuellen Status zu bekommen
    await store.update();
    
    const updatedProduct = store.get(productId);
    if (updatedProduct?.owned) {
      console.log('💳 Kauf erfolgreich! Produkt ist jetzt owned.');
      return { success: true, plan: plan };
    }
    
    // Wenn noch nicht owned, war es vermutlich cancelled
    console.log('💳 Produkt ist nicht owned nach Kauf-Versuch');
    return { success: false, error: 'cancelled' };

  } catch (error) {
    console.error('💳 Kauf-Fehler:', error);
    
    // User cancelled
    if (error.code === 6777001 || error.message?.includes('cancel')) {
      return { success: false, error: 'cancelled' };
    }
    
    return { 
      success: false, 
      error: error.message || tGlobal('ui.svc.purchaseUnknownError') 
    };
  }
}

/**
 * Stellt frühere Käufe wieder her
 * Nützlich wenn User App neu installiert oder Gerät wechselt
 */
export async function restorePurchases() {
  if (!isStoreReady || !store) {
    return { success: false, isPremium: false };
  }

  try {
    console.log('💳 Stelle Käufe wieder her...');
    
    // Refresh um Käufe vom Store zu laden
    await store.restorePurchases();

    // Prüfe ob aktive Subscriptions vorhanden
    const monthlyProduct = store.get(PRODUCT_IDS.MONTHLY);
    const yearlyProduct = store.get(PRODUCT_IDS.YEARLY);

    const hasMonthly = monthlyProduct?.owned;
    const hasYearly = yearlyProduct?.owned;

    if (hasMonthly || hasYearly) {
      console.log('💳 Aktive Subscription gefunden!');
      return { 
        success: true, 
        isPremium: true,
        plan: hasYearly ? 'yearly' : 'monthly',
      };
    } else {
      console.log('💳 Keine aktiven Käufe gefunden');
      return { success: true, isPremium: false };
    }

  } catch (error) {
    console.error('💳 Fehler beim Wiederherstellen:', error);
    return { success: false, isPremium: false, error: error.message };
  }
}

/**
 * Prüft aktuellen Premium-Status
 */
export async function checkPremiumStatus() {
  if (!isStoreReady || !store) {
    return { isPremium: false };
  }

  try {
    const monthlyProduct = store.get(PRODUCT_IDS.MONTHLY);
    const yearlyProduct = store.get(PRODUCT_IDS.YEARLY);

    const hasMonthly = monthlyProduct?.owned;
    const hasYearly = yearlyProduct?.owned;

    if (hasMonthly || hasYearly) {
      return {
        isPremium: true,
        plan: hasYearly ? 'yearly' : 'monthly',
      };
    }

    return { isPremium: false };

  } catch (error) {
    console.error('💳 Fehler beim Status-Check:', error);
    return { isPremium: false };
  }
}

/**
 * Öffnet das Abo-Management (Google Play Abos)
 */
export async function openSubscriptionManagement() {
  // Deep Link zu Google Play Subscriptions
  const url = 'https://play.google.com/store/account/subscriptions';
  
  if (Capacitor.isNativePlatform()) {
    // Auf Android: In externem Browser öffnen
    window.open(url, '_system');
  } else {
    window.open(url, '_blank');
  }
}

/**
 * Prüft ob Käufe verfügbar sind (native Plattform)
 */
export function isPurchaseAvailable() {
  return Capacitor.isNativePlatform() && isStoreReady;
}

export default {
  initializePurchases,
  getProducts,
  purchaseSubscription,
  restorePurchases,
  syncOwnedToServer,
  checkPremiumStatus,
  openSubscriptionManagement,
  isPurchaseAvailable,
  PRODUCT_IDS,
};
